import ChevronSmall from "@DSG/Icons/ChevronSmall";
import VideoPlay from "@DSG/Icons/VideoPlay";
import { AccordionSectionPresentationProps } from "@DSG/MobiusOverrides/AccordionSection/AccordionSection";
import ChevronRight from "@DSG/MobiusOverrides/Icons/ChevronRight";
import colorPallete, {
    bodyFonts,
    buttonFonts,
    elevation,
    headlineFonts,
    labelFonts,
    themeFontWeights,
} from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import File from "@insite/mobius/Icons/File";
import Img from "@insite/mobius/Img/Img";
import { Button, FileUpload } from "@insite/mobius/index";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Tab from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC, useEffect, useRef, useState } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    tutorialData = "tutorialData",
    viewLessText = "viewLessText",
    viewMoreText = "viewMoreText"
}

interface WebsiteTutorialItem {
    fields: {
        tutorialTitle: string;
        tutorialHeadingSubText: string;
        videos: VideoItem[];
        instructionLinkText: string;
        instructionLinkUrl: string;
        openInNewWindowInstructionLink: boolean;
        tutorialBottomSubText: string;
    };
}

interface VideoItem {
    fields: {
        videoTutorialTitle: string;
        videoTutorialHeadingSubText: string;
        videoThumbnailImage: string;
        videoThumbnailImageAltText: string;
        videoUrl: string;
        videoInstructionLinkText: string;
        videoInstructionLinkUrl: string;
        openInNewWindowInstructionLink: boolean;
        videoTutorialBottomSubText: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tutorialData]: WebsiteTutorialItem[];
        [fields.viewLessText]: string;
        [fields.viewMoreText]: string;
    };
}

export interface WebsiteTutorialStyles {
    tutorialTabGroup?: TabGroupPresentationProps;
    tutorialWrapper?: InjectableCss;
    tutorialBtn?: InjectableCss;
    tutorialDropdownWrapper?: InjectableCss;
    tutorialMainHeading?: InjectableCss;
    tutorialTabTitle?: InjectableCss;
    videoWrapper?: InjectableCss;
    downloadLinkWrapper?: InjectableCss;
    belowHeadingSubTextWrapper?: InjectableCss;
    videoInnerListWrapper?: InjectableCss;
    modal?: ModalPresentationProps;
    bottomTextWrapper?: InjectableCss;
    fullScreenMultiVideoContent?: InjectableCss;
    linkBtn?: InjectableCss;
}

export const websiteTutorialStyles: WebsiteTutorialStyles = {
    tutorialTabGroup: {
        mergeCss: true,
        cssOverrides: {
            wrapper: css`
                ol[class*="OrderedListStyle"] {
                    padding-left: 22px;
                    margin-top: 0;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                flex-direction: row;
                                align-items: flex-start;
                                column-gap: 40px;
                                [data-id="tabContent"] {
                                    min-width: calc(100% - 343px);
                                    max-width: calc(100% - 343px);
                                }
                            `,
                        ],
                        "min",
                    )}
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                margin-top: 48px;
                                [class*="TabGroupStyle"] {
                                    display: none;
                                }
                            `,
                        ],
                        "max",
                    )}

                .videoThumnailImageIconWrapper {
                    position: relative;
                    cursor: pointer;
                    img {
                        width: 100%;
                    }
                    svg {
                        position: absolute;
                        left: 0;
                        right: 0;
                        top: 0;
                        bottom: 0;
                        margin: auto;
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    svg {
                                        max-width: 72px;
                                        max-height: 72px;
                                    }
                                `,
                            ],
                            "max",
                        )}
                }
            `,
            tabGroup: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                flex-direction: column;
                                flex-wrap: wrap;
                                flex: initial;
                                min-width: 303px;
                                max-width: 303px;
                                border-radius: 8px;
                                padding: 16px 0 24px;
                                border: 1px solid ${colorPallete.grey300};
                                > li {
                                    text-align: left;
                                    padding: 10px 40px;
                                    font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize} /
                                        ${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily};
                                    color: ${colorPallete.grey800};
                                    border: none;
                                    cursor: pointer;
                                    &[aria-selected="true"],
                                    &:hover {
                                        background: ${colorPallete.grey100};
                                        font-weight: ${themeFontWeights.semiBold};
                                        position: relative;
                                        &:before {
                                            border-left: 4px solid ${colorPallete.primaryRed500};
                                            content: "";
                                            width: 4px;
                                            position: absolute;
                                            left: 0;
                                            height: 100%;
                                            top: 0;
                                        }
                                    }
                                    &:hover {
                                        border-bottom: none;
                                        outline: none;
                                    }
                                    &:focus {
                                        border: none;
                                    }
                                    &,
                                    &[aria-selected="true"] {
                                        &:focus,
                                        &:hover {
                                            &:before {
                                                border-left: 4px solid ${colorPallete.primaryRed500};
                                            }
                                        }
                                    }
                                }
                            `,
                        ],
                        "min",
                    )}
            `,
            tabContent: css`
                margin: 0;
                border: none;
                padding: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                flex-direction: column;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
    },
    tutorialBtn: {
        css: css`
            &[class*="ButtonWrapper"] {
                border: 1px solid ${colorPallete.grey400};
                border-radius: 8px;
                padding: 8px 16px;
                display: flex;
                align-items: center;
                background: ${colorPallete.white};
                box-shadow: ${elevation.dp04};
                color: ${colorPallete.grey800};
                font: ${bodyFonts.body1SemiBoldSm.fontWeight} ${bodyFonts.body1SemiBoldSm.fontSize} /
                    ${bodyFonts.body1SemiBoldSm.lineHeight} ${bodyFonts.body1SemiBoldSm.fontFamily};
                width: 100%;
                justify-content: space-between;
                min-height: 48px;
                text-align: left;
                &.drodpownOpen {
                    border-radius: 8px 8px 0 0;
                    svg {
                        transform: rotate(-180deg);
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                display: none;
                            `,
                        ],
                        "min",
                    )}
            }
        `,
    },
    tutorialWrapper: {
        css: css`
            margin: 40px 0;
            > ul {
                font: ${bodyFonts.body1SemiBoldSm.fontWeight} ${bodyFonts.body1SemiBoldSm.fontSize} /
                    ${bodyFonts.body1SemiBoldSm.lineHeight} ${bodyFonts.body1SemiBoldSm.fontFamily};
                padding: 16px 0;
                border-radius: 0 0 8px 8px;
                border: 1px solid ${colorPallete.grey400};
                border-top: none;
                background: ${colorPallete.white};
                display: none;
                &.dropDownListOpen {
                    display: block;
                }
                li {
                    font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize} /
                        ${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily};
                    padding: 9px 16px;
                    min-height: 40px;
                    &.selected {
                        background: ${colorPallete.grey100};
                    }
                }
            }
            .ellipsisText {
                align-items: center;
                display: flex;
                line-height: 20px;
                margin-bottom: 16px;
                margin-top: 16px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            margin: 24px 0;
                            > ul {
                                &.dropDownListOpen {
                                    display: none;
                                }
                            }
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            .tabInnerContentWrapper {
                                .ellipsisText {
                                    top: -16px;
                                    position: relative;
                                    margin-bottom: 0;
                                }
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    tutorialMainHeading: {
        css: css`
            font: ${headlineFonts.headline3SemiBoldSm.fontWeight} ${headlineFonts.headline3SemiBoldSm.fontSize} /
                ${headlineFonts.headline3SemiBoldSm.lineHeight} ${headlineFonts.headline3SemiBoldSm.fontFamily};
            color: ${colorPallete.grey800};
            margin-bottom: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${headlineFonts.headline3SemiBoldLg.fontSize};
                            line-height: ${headlineFonts.headline3SemiBoldLg.lineHeight};
                        `,
                    ],
                    "min",
                )}
        `,
    },
    tutorialTabTitle: {
        css: css`
            font: ${headlineFonts.headline4SemiBoldSm.fontWeight} ${headlineFonts.headline4SemiBoldSm.fontSize} /
                ${headlineFonts.headline4SemiBoldSm.lineHeight} ${headlineFonts.headline4SemiBoldSm.fontFamily};
            color: ${colorPallete.grey800};
            margin-bottom: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${headlineFonts.headline4SemiBoldLg.fontSize};
                            line-height: ${headlineFonts.headline4SemiBoldLg.lineHeight};
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    videoWrapper: {
        css: css`
            margin-bottom: 24px;
            display: flex;
            gap: 24px;
            flex-wrap: wrap;
            > div {
                width: 100%;
            }
            .singleVideoWrapper {
                border-radius: 8px;
                border: 1px solid ${colorPallete.grey500};
                width: 100%;
                > img {
                    border-radius: 8px;
                    display: block;
                    min-height: 192px;
                    object-fit: cover;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 612px;
                                min-height: 344px;
                                > img {
                                    min-height: 344px;
                                }
                            `,
                        ],
                        "min",
                    )}
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            gap: 40px;
                        `,
                        css`
                            > .videoInnerListWrapper {
                                max-width: 340px;
                                width: calc(100% / 2 - 40px);
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    downloadLinkWrapper: {
        css: css`
            margin-bottom: 32px;
            [class*="StyledClickable"] span {
                display: flex;
                gap: 8px;
                align-items: center;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            margin-bottom: 48px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    belowHeadingSubTextWrapper: {
        css: css`
            margin-bottom: 24px;
            p {
                font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize} /
                    ${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily};
                color: ${colorPallete.grey800};
                &:last-child {
                    margin-bottom: 0;
                }
            }
            &.videoTutorialSubText {
                margin-bottom: 0;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            margin-bottom: 48px;
                            p {
                                font-size: ${bodyFonts.body2RegularLg.fontSize};
                                line-height: ${bodyFonts.body2RegularLg.lineHeight};
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    videoInnerListWrapper: {
        css: css`
            border-radius: 8px;
            border: 1px solid ${colorPallete.grey500};
            background: ${colorPallete.white};
            box-shadow: ${elevation.dp04};
            padding: 16px;
            display: flex;
            flex-direction: column;
            h4 {
                margin-bottom: 8px;
                font: ${bodyFonts.body2SemiBoldSm.fontWeight} ${bodyFonts.body2SemiBoldSm.fontSize} /
                    ${bodyFonts.body2SemiBoldSm.lineHeight} ${bodyFonts.body2SemiBoldSm.fontFamily};
                color: ${colorPallete.primaryRed500};
            }
            .videoTutorialSubText {
                &,
                * {
                    font: ${labelFonts.label2Regular.fontWeight} ${labelFonts.label2Regular.fontSize} /
                        ${labelFonts.label2Regular.lineHeight} ${labelFonts.label2Regular.fontFamily};
                    color: ${colorPallete.grey800};
                }
            }
            .videoThumnailImageIconWrapper {
                border-radius: 2px;
                border: 1px solid ${colorPallete.grey300};
                margin-bottom: 16px;
                cursor: pointer;
            }
            .multipleThumbnailImage {
                img {
                    min-height: 173px;
                    max-height: 173px;
                    object-fit: cover;
                    display: block;
                }
                svg {
                    max-width: 72px;
                    max-height: 72px;
                }
            }
        `,
    },
    modal: {
        cssOverrides: {
            scrim: css`
                background: rgba(27, 28, 28, 0.7);
            `,
            modalTitle: css`
                padding: 0 !important;
                position: fixed;
                max-width: 1151px;
                margin: -32px auto 0;
                width: 100%;
                [data-test-selector="modalCloseButton"] {
                    padding-right: 8px;
                    &,
                    &:hover,
                    &:focus {
                        background: transparent !important;
                        border: none;
                        span[class*="IconWrapper"] {
                            color: ${colorPallete.white} !important;
                        }
                    }
                    #close-modal {
                        position: initial;
                        width: auto;
                        height: auto;
                        overflow: visible;
                        clip-path: initial;
                        margin: auto;
                        color: ${colorPallete.white};
                        font: ${bodyFonts.body2SemiBoldSm.fontWeight} ${bodyFonts.body2SemiBoldSm.fontSize} /
                            ${bodyFonts.body2SemiBoldSm.lineHeight} ${bodyFonts.body2SemiBoldSm.fontFamily};
                    }
                }
                @media screen and (min-width: 768px) and (max-width: 1279px) {
                    max-width: calc(100% - 90px);
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                [data-test-selector="modalCloseButton"] {
                                    padding-right: 0;
                                    #close-modal {
                                        font-size: ${bodyFonts.body2SemiBoldLg.fontSize};
                                        line-height: ${bodyFonts.body2SemiBoldLg.lineHeight};
                                    }
                                }
                            `,
                        ],
                        "min",
                    )}
            `,
            modalContent: css`
                padding: 0 !important;
            `,
            modalBody: css`
                border: 1px solid ${colorPallete.grey800};
            `,
            modalContainer: css`
                [class*="ContentBody"] {
                    border-radius: 0;
                    max-width: 1151px;
                    height: auto;
                    iframe {
                        width: 100%;
                        height: 219px;
                        display: flex;
                        border: none;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                [class*="ContentBody"] {
                                    iframe {
                                        height: 522px;
                                    }
                                }
                            `,
                        ],
                        "min",
                    )}

                @media only screen and (min-width: 1367px) {
                    [class*="ContentBody"] {
                        iframe {
                            height: 647px;
                        }
                    }
                }
            `,
        },
    },
    bottomTextWrapper: {
        css: css`
            ol, ul {
                margin-top: 0;
            }
            p,
            ul,
            li {
                font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize} /
                    ${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily};
                color: ${colorPallete.grey800};
            }
            p {
                margin-bottom: 16px;
            }
            ul {
                ul {
                    padding-left: 22px;
                }
            }
            > p {
                &:last-child {
                    margin-bottom: 0;
                }
            }
            strong {
                font-weight: ${themeFontWeights.semiBold};
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &.textFail {
                                -webkit-line-clamp: 5 !important;
                            }
                            &.textPass {
                                -webkit-line-clamp: initial !important;
                            }
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            &.textFail {
                                max-height: calc(5 * 24px) !important;
                            }
                            &.textPass {
                                max-height: initial !important;
                                margin-bottom: 16px;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    fullScreenMultiVideoContent: {
        css: css`
            .videoHeadingWrapper {
                margin-bottom: 24px;
                h4 {
                    font: ${headlineFonts.headline6SemiBoldLg.fontWeight} ${headlineFonts.headline6SemiBoldLg.fontSize} /
                        ${headlineFonts.headline6SemiBoldLg.lineHeight} ${headlineFonts.headline6SemiBoldLg.fontFamily};
                    color: ${colorPallete.grey800};
                    margin-bottom: 0;
                }
                > div {
                    width: 100%;
                }
                .videoDescription {
                    &,
                    p {
                        font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize} /
                            ${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily};
                        color: ${colorPallete.grey800};
                    }
                    p {
                        &:last-child {
                            margin-bottom: 0;
                        }
                    }
                }
                button {
                    font: ${bodyFonts.body2SemiBoldSm.fontWeight} ${bodyFonts.body2SemiBoldSm.fontSize} /
                        ${bodyFonts.body2SemiBoldSm.lineHeight} ${bodyFonts.body2SemiBoldSm.fontFamily};
                    color: ${colorPallete.primaryRed500};
                    margin-bottom: 28px;
                    padding: 0;
                    margin: 0;
                    white-space: nowrap;
                    display: flex;
                    align-items: center;
                    &,
                    &:hover,
                    &:focus {
                        background: transparent;
                        border: none;
                    }
                    [class*="IconWrapper"] {
                        margin-left: 4px;
                    }
                    svg {
                        transform: rotate(180deg);
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .videoHeadingWrapper {
                                display: flex;
                                flex-direction: row-reverse;
                                align-items: flex-start;
                                > div {
                                    padding-right: 20px;
                                }
                            }
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .videoHeadingWrapper {
                                button {
                                    margin-top: -24px;
                                    margin-bottom: 24px;
                                    min-height: inherit;
                                }
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    linkBtn: {
        css: css`
            span {
                color: ${colorPallete.primaryRed500};
                font: ${buttonFonts.buttonSm.fontWeight} ${buttonFonts.buttonSm.fontSize} /
                    ${buttonFonts.buttonSm.lineHeight} ${buttonFonts.buttonSm.fontFamily};
                text-decoration: underline;
                &:hover {
                    color: ${colorPallete.primaryMaroon500};
                }
            }
            &[class*="ButtonWrapper"] {
                min-height: initial;
                padding: 0;
                margin-top: 0;
                border-radius: 0;
            }
            &,
            &:hover,
            &:focus {
                background: transparent;
                border: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &[class*="ButtonWrapper"] {
                                margin-top: 32px;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = websiteTutorialStyles;
interface LinkProps {
    linkUrl: string;
    openInNewWindow: boolean;
}

export const WebsiteTutorial: FC<OwnProps> = ({ fields }) => {
    const { tutorialData } = fields;
    const tabs = [] as any;
    const [activeTab, setActiveTab] = useState<number | null>(0);
    const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);
    const [hideContentOnImageClick, setHideContentOnImageClick] = useState(true);
    const [showContent, setShowContent] = useState<boolean>(true);
    const [isListVisible, setIsListVisible] = useState(false);
    const activeTabName = tutorialData[activeTab || 0]?.fields?.tutorialTitle || "";

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [videoUrl, setVideoUrl] = useState("");
    const [expanded, setExpanded] = useState(false);

    const containerStyle = {
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        "-webkit-box-orient": "vertical",
    };

    const openModal = (videoLink: any) => {
        setModalIsOpen(true);
        setVideoUrl(videoLink);
    };

    const closeModal = () => {
        setModalIsOpen(false);
    };

    const handleTabChange = (index: number) => {
        setActiveTab(index);
        setShowContent(true);
        setActiveVideoIndex(null);
        setIsListVisible(false);
        setExpanded(false);
    };

    const handleThumbnailClick = (videoIndex: number) => {
        setActiveVideoIndex(videoIndex);
        setShowContent(false);
    };
    const handleBackButtonClick = () => {
        setShowContent(true);
        setActiveVideoIndex(null);
        setExpanded(false);
    };
    const CustomLink: FC<LinkProps> = ({ linkUrl, openInNewWindow, children }) => {
        return (
            <Link className="text-link text-link-primary" href={linkUrl} target={openInNewWindow ? "_blank" : ""}>
                {children}
            </Link>
        );
    };
    const toggleListVisibility = () => {
        setIsListVisible(!isListVisible);
    };

    {
        tutorialData.map((item, index) =>
            tabs.push(
                <Tab headline={item.fields.tutorialTitle} tabKey={`Tab${index}`} onClick={() => handleTabChange(index)}>
                    <Typography as="h4" variant="h4" {...styles.tutorialTabTitle}>
                        {item.fields.tutorialTitle}
                    </Typography>
                    {showContent && (
                        <StyledWrapper className="tabInnerContentWrapper">
                            {item.fields.tutorialHeadingSubText && (
                                <StyledWrapper {...styles.belowHeadingSubTextWrapper}>
                                    {parse(item.fields.tutorialHeadingSubText, parserOptions)}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.videoWrapper}>
                        {item.fields.videos &&
                            item.fields.videos.map((video, videoIndex) => (
                                <>
                                    {item.fields.videos.length === 1 && (
                                        <StyledWrapper
                                            key={`video${videoIndex}`}
                                            className="videoThumnailImageIconWrapper singleVideoWrapper"
                                            onClick={() => openModal(video.fields.videoUrl)}
                                        >
                                            <Img src={video.fields.videoThumbnailImage}    
                                                altText={video.fields.videoThumbnailImageAltText || translate("Play Video by clicking on thumbnail image and get info about ") + item.fields.tutorialTitle}/>
                                            <VideoPlay />
                                        </StyledWrapper>
                                    )}
                                    {item.fields.videos.length > 1 && (
                                        <>
                                            {hideContentOnImageClick && showContent && (
                                                <StyledWrapper
                                                    key={`video${videoIndex}`}
                                                    className="videoInnerListWrapper"
                                                    {...styles.videoInnerListWrapper}
                                                >
                                                    <StyledWrapper
                                                        onClick={() => handleThumbnailClick(videoIndex)}
                                                        className="videoThumnailImageIconWrapper multipleThumbnailImage"
                                                    >
                                                        <Img
                                                            src={video.fields.videoThumbnailImage}
                                                            altText={video.fields.videoThumbnailImageAltText || translate("Click on the thumbnail image to see more info about") + video.fields.videoTutorialTitle}
                                                        />
                                                        <VideoPlay />
                                                    </StyledWrapper>
                                                    <Typography as="h4" variant="h4">
                                                        {`${videoIndex + 1}. `} {video.fields.videoTutorialTitle}
                                                    </Typography>
                                                    <StyledWrapper
                                                        {...styles.belowHeadingSubTextWrapper}
                                                        className="videoTutorialSubText"
                                                    >
                                                        {parse(video.fields.videoTutorialHeadingSubText, parserOptions)}
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            )}
                                            {activeVideoIndex === videoIndex && (
                                                <StyledWrapper
                                                    key={`video${videoIndex}`}
                                                    {...styles.fullScreenMultiVideoContent}
                                                >
                                                    <StyledWrapper className="videoHeadingWrapper">
                                                        <Button onClick={handleBackButtonClick}>
                                                            <ChevronRight /> {translate("Back")}
                                                        </Button>
                                                        <StyledWrapper>
                                                            <Typography as="h4" variant="h4">
                                                                {`${videoIndex + 1}. `}
                                                                {video.fields.videoTutorialTitle}
                                                            </Typography>
                                                            <StyledWrapper className="videoDescription">
                                                                {parse(
                                                                    video.fields.videoTutorialHeadingSubText,
                                                                    parserOptions,
                                                                )}
                                                            </StyledWrapper>
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                    <StyledWrapper {...styles.videoWrapper}>
                                                        <StyledWrapper
                                                            className="videoThumnailImageIconWrapper singleVideoWrapper"
                                                            onClick={() => openModal(video.fields.videoUrl)}
                                                        >
                                                            <Img
                                                                src={video.fields.videoThumbnailImage}
                                                                altText={video.fields.videoThumbnailImageAltText || ''}
                                                            />
                                                            <VideoPlay />
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                    {video.fields.videoInstructionLinkText && (
                                                        <StyledWrapper {...styles.downloadLinkWrapper}>
                                                            <CustomLink
                                                                linkUrl={video.fields.videoInstructionLinkUrl}
                                                                openInNewWindow={true}
                                                            >
                                                                <File />
                                                                {video.fields.videoInstructionLinkText}
                                                            </CustomLink>
                                                        </StyledWrapper>
                                                    )}

                                                    {video.fields.videoTutorialBottomSubText && (
                                                        <>
                                                            <StyledWrapper
                                                                {...styles.bottomTextWrapper}
                                                                id="bottomTextWrapper"
                                                                style={containerStyle}
                                                                className={expanded ? "textPass" : "textFail"}
                                                            >
                                                                {parse(
                                                                    video.fields.videoTutorialBottomSubText,
                                                                    parserOptions,
                                                                )}
                                                            </StyledWrapper>
                                                            {!expanded && (
                                                                <Typography className="ellipsisText mobileSpecific">
                                                                    ...
                                                                </Typography>
                                                            )}
                                                            {video.fields.videoTutorialBottomSubText &&
                                                                video.fields.videoTutorialBottomSubText.split("\n")
                                                                    .length >= 5 && (
                                                                    <Button
                                                                        {...styles.linkBtn}
                                                                        onClick={() => setExpanded(!expanded)}
                                                                    >
                                                                        {expanded
                                                                            ? fields.viewLessText
                                                                            : fields.viewMoreText
                                                                        }
                                                                    </Button>
                                                                )}
                                                        </>
                                                    )}
                                                </StyledWrapper>
                                            )}
                                        </>
                                    )}
                                </>
                            ))}
                    </StyledWrapper>
                    {showContent && (
                        <StyledWrapper className="tabInnerContentWrapper">
                            {item.fields.instructionLinkText && (
                                <StyledWrapper {...styles.downloadLinkWrapper}>
                                    <CustomLink linkUrl={item.fields.instructionLinkUrl} openInNewWindow={true}>
                                        <File />
                                        {item.fields.instructionLinkText}
                                    </CustomLink>
                                </StyledWrapper>
                            )}
                            {item.fields.tutorialBottomSubText && (
                                <>
                                    <StyledWrapper
                                        {...styles.bottomTextWrapper}
                                        id="bottomTextWrapper"
                                        style={containerStyle}
                                        className={expanded ? "textPass" : "textFail"}
                                    >
                                        {parse(item.fields.tutorialBottomSubText, parserOptions)}
                                    </StyledWrapper>
                                    {!expanded && <Typography className="ellipsisText mobileSpecific">...</Typography>}
                                    {item.fields.tutorialBottomSubText &&
                                        item.fields.tutorialBottomSubText.split("\n").length >= 5 && (
                                            <Button {...styles.linkBtn} onClick={() => setExpanded(!expanded)}>
                                                {expanded
                                                    ? fields.viewLessText
                                                    : fields.viewMoreText}
                                            </Button>
                                        )}
                                </>
                            )}
                        </StyledWrapper>
                    )}
                </Tab>,
            ),
        );
    }

    return (
        <StyledWrapper {...styles.tutorialWrapper}>
            <Typography as="h3" variant="h3" {...styles.tutorialMainHeading}>
                {fields.title}
            </Typography>
            <Button
                onClick={toggleListVisibility}
                {...styles.tutorialBtn}
                className={`${isListVisible ? "drodpownOpen" : ""}`}
            >
                {activeTabName}
                <ChevronSmall />
            </Button>
            <ul className={`${isListVisible ? "dropDownListOpen" : ""}`}>
                {tutorialData.map((item, index) => (
                    <li
                        key={index}
                        onClick={() => handleTabChange(index)}
                        className={index === activeTab ? "selected" : ""}
                    >
                        {item.fields.tutorialTitle}
                    </li>
                ))}
            </ul>
            <TabGroup current={`Tab${activeTab}`} {...styles.tutorialTabGroup}>
                {tabs}
            </TabGroup>
            <Modal isOpen={modalIsOpen} handleClose={closeModal} {...styles.modal}>
                <StyledWrapper className="video-container">
                    <iframe
                        title="Vimeo Video Player"
                        src={`${videoUrl}&autoplay=1`}
                        allow="autoplay; fullscreen; picture-in-picture"
                    ></iframe>
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: WebsiteTutorial,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.viewLessText,
                editorTemplate: "TextField",
                defaultValue: "View less",
                displayName: "View Less Label Text",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.viewMoreText,
                editorTemplate: "TextField",
                defaultValue: "View full instructions",
                displayName: "View More Label Text",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.tutorialData,
                editorTemplate: "ListField",
                displayName: "Tutorial Content",
                getDisplay: (item: HasFields, state) => {
                    const {
                        tutorialTitle,
                        tutorialHeadingSubText,
                        instructionLinkText,
                        instructionLinkUrl,
                        tutorialBottomSubText,
                        videos,
                    } = item.fields;
                    if (tutorialTitle) {
                        return tutorialTitle;
                    }
                    if (tutorialHeadingSubText) {
                        return tutorialHeadingSubText;
                    }
                    if (instructionLinkText) {
                        return instructionLinkText;
                    }

                    if (instructionLinkUrl) {
                        return instructionLinkText.value;
                    }
                    if (tutorialBottomSubText) {
                        return tutorialBottomSubText;
                    }
                    if (videos) {
                        return videos;
                    }
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 2,
                fieldDefinitions: [
                    {
                        name: "tutorialTitle",
                        editorTemplate: "TextField",
                        displayName: "Title",
                        defaultValue: "",
                        sortOrder: 0,
                        isRequired: true,
                    },
                    {
                        name: "tutorialHeadingSubText",
                        editorTemplate: "RichTextField",
                        displayName: "Below Heading Description",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        sortOrder: 1,
                    },
                    {
                        name: "instructionLinkText",
                        displayName: "Download Instruction Link Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "instructionLinkUrl",
                        displayName: "Download Instruction Link Url",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "tutorialBottomSubText",
                        editorTemplate: "RichTextField",
                        displayName: "Bottom Description",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        sortOrder: 1,
                    },
                    {
                        name: "videos",
                        editorTemplate: "ListField",
                        displayName: "Videos",
                        defaultValue: [],
                        getDisplay: (item: HasFields, state) => {
                            const { videoTutorialTitle, videoTutorialHeadingSubText } = item.fields;
                            if (videoTutorialTitle) {
                                return videoTutorialTitle;
                            }
                            if (videoTutorialHeadingSubText) {
                                return videoTutorialHeadingSubText;
                            }
                        },
                        fieldDefinitions: [
                            {
                                name: "videoThumbnailImage",
                                editorTemplate: "ImagePickerField",
                                displayName: "Thumbnail Image URL",
                                defaultValue: "",
                                sortOrder: 2,
                                isRequired: true,
                            },
                            {
                                name: "videoThumbnailImageAltText",
                                editorTemplate: "TextField",
                                displayName: "Thumbnail Image Alt Text",
                                defaultValue: "",
                                sortOrder: 2,
                            },
                            {
                                name: "videoUrl",
                                editorTemplate: "TextField",
                                displayName: "Video URL",
                                defaultValue: "",
                                sortOrder: 3,
                                isRequired: true,
                            },
                            {
                                name: "videoTutorialTitle",
                                editorTemplate: "TextField",
                                displayName: "Video Title",
                                defaultValue: "Video Tutorial Title",
                                sortOrder: 0,
                            },
                            {
                                name: "videoTutorialHeadingSubText",
                                editorTemplate: "RichTextField",
                                displayName: "Video Subtext",
                                defaultValue: "",
                                sortOrder: 1,
                            },
                            {
                                name: "videoInstructionLinkText",
                                displayName: "Download Instruction Link Text",
                                editorTemplate: "TextField",
                                defaultValue: "",
                                sortOrder: 5,
                            },
                            {
                                name: "videoInstructionLinkUrl",
                                displayName: "Download Instruction Link URL",
                                editorTemplate: "TextField",
                                defaultValue: "",
                                sortOrder: 6,
                            },
                            {
                                name: "openInNewWindowInstructionLink",
                                displayName: "Open in new window",
                                editorTemplate: "CheckboxField",
                                defaultValue: true,
                                sortOrder: 7,
                            },
                            {
                                name: "videoTutorialBottomSubText",
                                editorTemplate: "RichTextField",
                                displayName: "Bottom Description",
                                defaultValue: "",
                                sortOrder: 8,
                            },
                        ],
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
